import GoogletagFacade from '../googletagFacade/GoogletagFacade'
import Logger from '../helpers/Logger'
import WebModel from '../models/WebModel'
import { s2sBidder } from '../enums/Bidder'
import { getRandom } from '../helpers/array'

export const LIFT_TEST_PERCENT = 0.04 // 4 percent

const adXPartners = {
  rubicon: true,
  pubmatic: true,
  openx: true,
  indexExchange: true
}

interface ILiftTestGroup {
  bidder: string
  eb: boolean
  prebid: boolean
}

/**
 * This module performs tests to determine the lift of each bidder.
 * There is a 4% chance that a test will be performed.  If a test is performed
 * we pick at random from a list of tests which include valid bidders for the
 * site we are on, AdExchange bidders, and disabling both prebid AND Adx for a particular bidder.
 * Then there is a 50/50 chance that this run will be a control or a test run.
 * If we are not the control, we disable the bidder associated with the test.
 * 0 = test
 * 1 = control
 */
export default class LiftTest {
  liftValue?: string
  testGroup?: ILiftTestGroup

  constructor(model: WebModel, googletagFacade: GoogletagFacade) {
    const randomNumber = Math.random()
    // No test this time!
    if (randomNumber > LIFT_TEST_PERCENT) {
      return
    }

    const testGroups = LiftTest.buildTestGroups(model)
    this.testGroup = getRandom(testGroups)
    const testOrControl =
      Math.random() <= 0.5 ? '_0' /* test */ : '_1' /* control */

    this.liftValue = LiftTest.buildLiftValue(this.testGroup, testOrControl)
    const { bidder, eb, prebid } = this.testGroup

    Logger.debug(`
      Lift Test Results:
      Bidder ${bidder}
      Prebid: ${prebid ? 'Off' : 'On'}
      EB: ${eb ? 'Off' : 'On'}
    `)

    LiftTest.applyToBidRequests(model, this.testGroup)
    LiftTest.applyToGPT(this.liftValue, googletagFacade)
  }

  static applyToGPT(liftValue: string, googletagFacade: GoogletagFacade): void {
    GoogletagFacade.onReady<void>(() => {
      googletagFacade.pubads.setTargeting('partnerLift', liftValue)
    })
  }

  static applyToBidRequests(
    model: Pick<WebModel, 'bidRequests'>,
    { prebid, bidder }: ILiftTestGroup
  ) {
    if (!prebid) {
      return
    }
    // Remove the bidder from each bidRequest arary so we don't request bids from them
    Object.keys(model.bidRequests).forEach((adunit) => {
      model.bidRequests[adunit] = model.bidRequests[adunit].filter(
        (bid) => bid.bidder !== bidder
      )
    })
  }

  static buildLiftValue(
    { bidder, eb, prebid }: ILiftTestGroup,
    testOrControl: '_0' | '_1'
  ): string {
    let liftValue = bidder
    if (eb && prebid) {
      liftValue += '_both'
    }
    if (eb && !prebid) {
      liftValue += '_eb'
    }
    liftValue += testOrControl

    return liftValue
  }

  static buildTestGroups(model: WebModel): Array<ILiftTestGroup> {
    const potentialTests: Array<ILiftTestGroup> = []

    // Prebid bidders
    model.bidders.forEach(({ name: bidder }) => {
      // Bidders participating in both AdX and Prebid
      if (adXPartners[bidder]) {
        potentialTests.concat([
          { bidder, eb: true, prebid: true },
          // Test if they are better to be in EB alone
          { bidder, eb: true, prebid: false }
        ])
      }
      // All prebid bidders
      potentialTests.push({ bidder, eb: false, prebid: true })
    })

    // S2S Bidders
    Object.keys(s2sBidder).forEach((bidder) =>
      potentialTests.push({ bidder, eb: false, prebid: true })
    )

    // Test EB as a whle
    potentialTests.push({ bidder: 'eb', eb: true, prebid: false }) // Exchange Bidding

    return potentialTests
  }
}
